import React, { useState } from "react";
import "./css/tca_page3_graphs.scss";
import { useDocument } from "../../loan_tools/loan_tool";
import { useUser } from "../../../hooks/user";
import { loan_visible_for_viewer } from "../../../constants/lender_visibility";
import DateField, { today } from "../../fields/date_field";
import PhoneField from "../../fields/phone_field";
import Image from "../../ui/image";
import { borrower_names } from "../../../constants/utils";
import {
  loan_amount_first_mortgage,
  loan_term_in_years,
  total_closing_costs,
  first_mortgage_payment_p_and_i
} from "../../../calculations/loan";
import Decimal from "decimal.js";

const COLORS      = ["#006296", "#2980b9", "#8e44ad", "#d35400", "#c0392b"];
const GREEN       = "#27ae60";
const YEAR_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1);

function fmtFull(d) {
  return "$" + Math.round(d instanceof Decimal ? d.toNumber() : d).toLocaleString();
}
function fmtK(d) {
  const n = Math.round(d instanceof Decimal ? d.toNumber() : d);
  return n >= 1000 ? "$" + (n / 1000).toFixed(0) + "k" : "$" + n;
}

function balAfter(loanAmt, r, n, k) {
  if (k >= n) return new Decimal(0);
  if (r.eq(0))  return loanAmt.mul(n - k).div(n);
  const P1r = r.add(1);
  const P1rN = P1r.pow(n);
  return loanAmt.mul(P1rN.sub(P1r.pow(k))).div(P1rN.sub(1));
}

function buildStats(documentType, loan, property, pts, isp, stayYrs) {
  const loanAmt    = loan_amount_first_mortgage(documentType, loan, property, pts, isp);
  const monthlyPI  = first_mortgage_payment_p_and_i(documentType, loan, property, pts, isp);
  const closing    = total_closing_costs(documentType, loan, property, pts, isp);
  const termYrs    = loan_term_in_years(loan);
  const n          = Math.round(termYrs.mul(12).toNumber());
  const r          = new Decimal(loan.rate || 0).div(100).div(12);
  const P1rN       = r.eq(0) ? null : r.add(1).pow(n);
  const denom      = r.eq(0) ? new Decimal(n) : P1rN.sub(1);

  function statsAt(yr) {
    const k   = Math.min(yr * 12, n);
    const bal = k >= n ? new Decimal(0)
      : r.eq(0) ? loanAmt.mul(n - k).div(denom)
      : loanAmt.mul(P1rN.sub(r.add(1).pow(k))).div(denom);
    const principalPaid = loanAmt.sub(bal);
    const totalPIPaid   = monthlyPI.mul(k);
    const interestPaid  = totalPIPaid.sub(principalPaid);
    const totalCost     = totalPIPaid.add(closing);
    return { principalPaid, interestPaid, totalPIPaid, totalCost };
  }

  function splitAt(month) {
    if (month < 1 || month > n) return { principal: new Decimal(0), interest: new Decimal(0) };
    const balBefore = month === 1 ? loanAmt
      : r.eq(0) ? loanAmt.mul(n - (month - 1)).div(denom)
      : loanAmt.mul(P1rN.sub(r.add(1).pow(month - 1))).div(denom);
    const interest  = balBefore.mul(r);
    const principal = monthlyPI.sub(interest);
    return { principal: principal.gt(0) ? principal : new Decimal(0), interest };
  }

  const s = statsAt(stayYrs);
  return {
    loanAmt, monthlyPI, closing, termYrs, n, r,
    rate: new Decimal(loan.rate || 0).toNumber(),
    ...s, statsAt, splitAt,
  };
}

// ── Chart: Vertical Stacked Bars ─────────────────────────────────────────────

function BarChart({ data, bestIdx, stayYears }) {
  const maxTotal  = data.reduce((m, d) => d.totalCost.gt(m) ? d.totalCost : m, new Decimal(0));
  if (maxTotal.eq(0)) return <div className="g3-empty">No loan data available.</div>;
  const minTotal  = data.reduce((m, d) => d.totalCost.lt(m) ? d.totalCost : m, maxTotal);
  const delta     = maxTotal.sub(minTotal).toNumber();
  const dpad      = Math.max(delta * 0.35, 500);
  const axisMin   = new Decimal(Math.max(0, minTotal.toNumber() - dpad));
  const axisMax   = new Decimal(maxTotal.toNumber() + dpad);
  const axisRange = axisMax.sub(axisMin);

  const barW = 72, gap = 18, padL = 56, padB = 52, chartH = 220;
  const totalW = padL + data.length * (barW + gap) + gap;
  const svgH = chartH + padB;

  return (
    <svg viewBox={`0 0 ${totalW} ${svgH}`} width="100%" className="g3-bar-svg">
      {[0, 0.25, 0.5, 0.75, 1].map(pct => {
        const y   = chartH * (1 - pct);
        const val = axisMin.add(axisRange.mul(pct));
        return (
          <g key={pct}>
            <line x1={padL} y1={y} x2={totalW - gap} y2={y} stroke="#e8e8e8" />
            <text x={padL - 4} y={y + 4} textAnchor="end" fontSize={9} fill="#aaa">
              {fmtK(val)}
            </text>
          </g>
        );
      })}
      <line x1={padL} y1={0} x2={padL} y2={chartH} stroke="#ddd" />
      <line x1={padL} y1={chartH} x2={totalW - gap} y2={chartH} stroke="#ddd" />

      {data.map((d, i) => {
        const x      = padL + gap + i * (barW + gap);
        const isBest = i === bestIdx;
        const col    = isBest ? GREEN : (COLORS[i] || "#666");
        const totalH = axisRange.gt(0) ? d.totalCost.sub(axisMin).div(axisRange).mul(chartH).toNumber() : 0;
        const ccFrac = d.totalCost.gt(0) ? d.closing.div(d.totalCost).toNumber() : 0;
        const ccH    = totalH * ccFrac;
        const piH    = totalH - ccH;
        const barTop = chartH - totalH;

        return (
          <g key={i}>
            <rect x={x} y={chartH - ccH} width={barW} height={Math.max(ccH, 0)}
              fill={col} opacity={0.28} />
            <rect x={x} y={barTop} width={barW} height={Math.max(piH, 0)}
              fill={col} opacity={isBest ? 1 : 0.78} rx={2} />
            <text x={x + barW / 2} y={barTop - (isBest ? 18 : 7)} textAnchor="middle"
              fontSize={9} fill={isBest ? "#1b5e20" : "#555"}>
              {fmtFull(d.totalCost)}
            </text>
            {isBest && (
              <text x={x + barW / 2} y={barTop - 7} textAnchor="middle"
                fontSize={13} fill={GREEN}>★</text>
            )}
            <text x={x + barW / 2} y={chartH + 14} textAnchor="middle"
              fontSize={10} fontWeight={isBest ? "bold" : "normal"} fill={isBest ? "#1b5e20" : "#444"}>
              Loan {i + 1}
            </text>
            <text x={x + barW / 2} y={chartH + 25} textAnchor="middle"
              fontSize={9} fill="#999">{d.rate.toFixed(3)}%</text>
            <text x={x + barW / 2} y={chartH + 37} textAnchor="middle"
              fontSize={9} fill="#555">{fmtFull(d.monthlyPI)}/mo</text>
          </g>
        );
      })}

      <rect x={padL} y={svgH - 12} width={10} height={10} fill="#006296" opacity={0.85} rx={1} />
      <text x={padL + 14} y={svgH - 4} fontSize={9} fill="#666">P&amp;I payments</text>
      <rect x={padL + 110} y={svgH - 12} width={10} height={10} fill="#888" opacity={0.3} rx={1} />
      <text x={padL + 124} y={svgH - 4} fontSize={9} fill="#666">Closing costs</text>
      {axisMin.gt(0) && (
        <text x={totalW / 2} y={chartH - 5} textAnchor="middle" fontSize={8} fill="#bbb" fontStyle="italic">
          axis starts at {fmtK(axisMin)} — zoomed to show differences
        </text>
      )}
    </svg>
  );
}

// ── Chart: Donut Circles ──────────────────────────────────────────────────────

function DonutCharts({ data, bestIdx }) {
  return (
    <div className="g3-donuts">
      {data.map((d, i) => {
        const total  = d.totalCost;
        const isBest = i === bestIdx;
        const col    = isBest ? GREEN : (COLORS[i] || "#666");
        const prinPct = total.gt(0) ? d.principalPaid.div(total).mul(100).toNumber() : 0;
        const intPct  = total.gt(0) ? d.interestPaid.div(total).mul(100).toNumber()  : 0;
        const p2 = prinPct + intPct;
        const gradient = `conic-gradient(${col} 0% ${prinPct.toFixed(1)}%, #aaaaaa ${prinPct.toFixed(1)}% ${p2.toFixed(1)}%, #dddddd ${p2.toFixed(1)}% 100%)`;

        return (
          <div key={i} className={`g3-donut-card${isBest ? " g3-best" : ""}`}>
            <div className="g3-donut-name" style={{ color: col }}>
              Loan {i + 1}{isBest ? " ★" : ""}
            </div>
            <div className="g3-donut-rate">{d.rate.toFixed(3)}%</div>
            <div className="g3-donut-ring-wrap">
              <div className="g3-donut-ring" style={{ background: gradient }}>
                <div className="g3-donut-center">
                  <div className="g3-donut-total">{fmtFull(total)}</div>
                </div>
              </div>
            </div>
            <div className="g3-donut-monthly">{fmtFull(d.monthlyPI)}/mo</div>
            <div className="g3-donut-legend">
              <span style={{ color: col }}>■</span> Equity {Math.round(prinPct)}%<br />
              <span style={{ color: "#aaa" }}>■</span> Interest {Math.round(intPct)}%<br />
              <span style={{ color: "#ccc" }}>■</span> Closing {Math.round(100 - p2)}%
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Chart: Cost Over Time Line Chart ─────────────────────────────────────────

function LineChart({ data, bestIdx, stayYears }) {
  if (!data.length) return null;
  const YEARS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const padL = 58, padR = 32, padB = 30, chartH = 230;
  const svgW = 580;
  const plotW = svgW - padL - padR;

  const series = data.map(d => YEARS.map(yr => d.statsAt(yr).totalCost));
  const maxVal = series.reduce((m, s) =>
    s.reduce((m2, v) => v.gt(m2) ? v : m2, m), new Decimal(0));

  function toX(yr) { return padL + ((yr - 1) / (YEARS.length - 1)) * plotW; }
  function toY(v)  { return chartH - (maxVal.gt(0) ? v.div(maxVal).mul(chartH).toNumber() : 0); }

  return (
    <svg viewBox={`0 0 ${svgW} ${chartH + padB}`} width="100%" className="g3-line-svg">
      {[0, 0.25, 0.5, 0.75, 1].map(pct => {
        const y = chartH * (1 - pct);
        return (
          <g key={pct}>
            <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="#e8e8e8" />
            <text x={padL - 4} y={y + 4} textAnchor="end" fontSize={9} fill="#aaa">
              {fmtK(maxVal.mul(pct))}
            </text>
          </g>
        );
      })}
      {stayYears <= 10 && (
        <>
          <line x1={toX(stayYears)} y1={4} x2={toX(stayYears)} y2={chartH}
            stroke="#006296" strokeWidth={1.5} strokeDasharray="5,4" opacity={0.6} />
          <text x={toX(stayYears)} y={0} textAnchor="middle" fontSize={9} fill="#006296">
            Yr {stayYears}
          </text>
        </>
      )}
      {series.map((s, i) => {
        const isBest = i === bestIdx;
        const col    = isBest ? GREEN : (COLORS[i] || "#666");
        const pts    = YEARS.map(yr => `${toX(yr)},${toY(s[yr - 1])}`).join(" ");
        return (
          <g key={i}>
            <polyline points={pts} fill="none" stroke={col}
              strokeWidth={isBest ? 2.8 : 1.6} opacity={isBest ? 1 : 0.65} />
            <text x={toX(10) + 5} y={toY(s[9]) + 4} fontSize={9}
              fill={isBest ? "#1b5e20" : col} fontWeight={isBest ? "bold" : "normal"}>
              {isBest ? "★ " : ""}L{i + 1}
            </text>
          </g>
        );
      })}
      <line x1={padL} y1={chartH} x2={svgW - padR} y2={chartH} stroke="#ddd" />
      {YEARS.map(yr => (
        <text key={yr} x={toX(yr)} y={chartH + 14} textAnchor="middle"
          fontSize={9} fill="#aaa">Yr {yr}</text>
      ))}
    </svg>
  );
}

// ── Chart: Amortization Payment Breakdown ────────────────────────────────────

function AmortChart({ data, bestIdx }) {
  const best = data[bestIdx];
  if (!best) return <div className="g3-empty">No loan data available.</div>;

  const checkMonths  = [1, 36, 60, 84, 120, 180, 240, 360];
  const checkLabels  = ["Yr 1 (Mo. 1)", "Yr 3 (Mo. 36)", "Yr 5 (Mo. 60)", "Yr 7 (Mo. 84)",
                        "Yr 10 (Mo. 120)", "Yr 15 (Mo. 180)", "Yr 20 (Mo. 240)", "Yr 30 (Mo. 360)"];

  const rows = checkMonths
    .map((m, i) => {
      if (m > best.n) return null;
      const { principal, interest } = best.splitAt(m);
      const prinPct = best.monthlyPI.gt(0)
        ? Math.round(principal.div(best.monthlyPI).mul(100).toNumber()) : 0;
      return { label: checkLabels[i], principal, interest, prinPct };
    })
    .filter(Boolean);

  return (
    <div className="g3-amort-chart">
      <div className="g3-amort-subtitle">
        Loan {bestIdx + 1} at {best.rate.toFixed(3)}% — {fmtFull(best.monthlyPI)}/mo (constant payment throughout loan)
      </div>
      <table className="g3-amort-table">
        <thead>
          <tr>
            <th>Payment Period</th>
            <th>Monthly P&amp;I</th>
            <th className="g3-th-green">→ Principal (builds equity)</th>
            <th>→ Interest (to lender)</th>
            <th>% Building Equity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td>{row.label}</td>
              <td className="g3-right">{fmtFull(best.monthlyPI)}</td>
              <td className="g3-right g3-green-text">{fmtFull(row.principal)}</td>
              <td className="g3-right g3-gray-text">{fmtFull(row.interest)}</td>
              <td>
                <div className="g3-prog-wrap">
                  <div className="g3-prog-fill" style={{ width: row.prinPct + "%" }} />
                  <span className="g3-prog-pct">{row.prinPct}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="g3-amort-note">
        This is the mortgage amortization table behind all equity figures in these graphs.
        Early payments are mostly interest; later payments are mostly principal.
      </div>
    </div>
  );
}

// ── Amortization explanation + mini table (always shown) ─────────────────────

function AmortSection({ data, bestIdx }) {
  const best = data[bestIdx];
  if (!best) return null;

  const rows = [1, 36, 60, 120, 360].map(m => {
    if (m > best.n) return null;
    const { principal } = best.splitAt(m);
    const prinPct = best.monthlyPI.gt(0)
      ? Math.round(principal.div(best.monthlyPI).mul(100).toNumber()) : 0;
    const yr = m === 1 ? "Year 1" : m === 36 ? "Year 3" : m === 60 ? "Year 5"
      : m === 120 ? "Year 10" : "Year 30";
    return { yr, prinPct };
  }).filter(Boolean);

  return (
    <div className="g3-amort-section">
      <div className="g3-amort-heading">
        How "Equity Built" is Calculated — Mortgage Amortization Table
      </div>
      <p className="g3-amort-body">
        Each monthly payment is split between <strong>Principal</strong> (reduces the loan balance — builds equity)
        and <strong>Interest</strong> (the cost of borrowing). In the early years of a 30-year loan,
        most of each payment is interest — only a small portion reduces the balance.
        Over time this reverses. All equity figures in these graphs use the standard
        mortgage amortization schedule, not a 50/50 estimate.
      </p>
      <div className="g3-amort-pills">
        {rows.map((r, i) => (
          <div key={i} className="g3-amort-pill">
            <div className="g3-pill-label">{r.yr}</div>
            <div className="g3-pill-bar">
              <div className="g3-pill-fill" style={{ width: r.prinPct + "%" }} />
            </div>
            <div className="g3-pill-pct">{r.prinPct}% equity</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function TcaPage3Graphs({ yearCols = [3, 5, 7, 10], setYearCols = () => {} }) {
  const [{ companyProfile, accountProfile }] = useUser();
  const [
    { loans, property, property_to_be_sold, is_selling_property, applicant },
    ,
    { documentType, documentAccountProfile }
  ] = useDocument();

  const [graphType,    setGraphType]    = useState("bar");
  const [graphYearIdx, setGraphYearIdx] = useState(yearCols.length - 1);

  const safeIdx   = Math.min(graphYearIdx, yearCols.length - 1);
  const stayYears = yearCols[safeIdx];

  const borrowers      = (applicant && applicant.borrowers) || [];
  const firstBorrower  = borrowers[0] || {};
  const borrowerNames  = borrower_names(borrowers);
  const loName = [documentAccountProfile.first_name, documentAccountProfile.last_name]
    .filter(Boolean).join(" ");

  const activeLoan = (loans || []).filter(
    l => l && loan_visible_for_viewer(l, accountProfile)
  );
  const data = activeLoan.map(loan =>
    buildStats(documentType, loan, property, property_to_be_sold, is_selling_property, stayYears)
  );

  const bestIdx = data.length
    ? data.reduce((best, d, i) => d.totalCost.lt(data[best].totalCost) ? i : best, 0)
    : 0;

  const showStayPicker = graphType !== "amort";

  return (
    <div className="tca-page3-graphs">

      {/* ── Page Header ────────────────────────────────────────────────────── */}
      <div className="pa-header">
        <div className="pa-logo">
          <Image className="company-logo" url="company/company-logo" />
        </div>
        <div className="pa-borrower-info">
          <div className="pa-section-label">CLIENT(S)</div>
          <div className="pa-borrower-grid">
            <div className="pa-borrower-col">
              {borrowerNames[0] && <div className="pa-name">{borrowerNames[0]}</div>}
              {borrowerNames[1] && <div className="pa-name">{borrowerNames[1]}</div>}
            </div>
            {(borrowerNames[2] || borrowerNames[3]) && (
              <div className="pa-borrower-col">
                {borrowerNames[2] && <div className="pa-name">{borrowerNames[2]}</div>}
                {borrowerNames[3] && <div className="pa-name">{borrowerNames[3]}</div>}
              </div>
            )}
          </div>
          {firstBorrower.current_address && (
            <div className="pa-addr">{firstBorrower.current_address}</div>
          )}
          {firstBorrower.current_address_2 && (
            <div className="pa-addr">{firstBorrower.current_address_2}</div>
          )}
          <div className="pa-addr">
            {[firstBorrower.city, firstBorrower.state].filter(Boolean).join(", ")}
            {firstBorrower.zip_code ? " " + firstBorrower.zip_code : ""}
          </div>
        </div>
        <div className="pa-lo-dates">
          <div className="pa-lo-info">
            {loName && <div className="pa-lo-name">{loName}</div>}
            {documentAccountProfile.nmls_number && (
              <div>NMLS #: {documentAccountProfile.nmls_number}</div>
            )}
            {documentAccountProfile.state_real_estate_license && (
              <div>Lic #: {documentAccountProfile.state_real_estate_license}</div>
            )}
            {documentAccountProfile.direct_contact_phone && (
              <div className="pa-phone-row">
                Cell: <PhoneField naked value={documentAccountProfile.direct_contact_phone} />
              </div>
            )}
            {documentAccountProfile.email && <div>{documentAccountProfile.email}</div>}
          </div>
          <div className="pa-dates">
            <div>
              <span className="pa-date-label">DATE FIRST ISSUED:</span>
              <DateField naked value={applicant && applicant.first_contact_date} />
            </div>
            <div>
              <span className="pa-date-label">DATE LAST ISSUED:</span>
              <DateField naked value={today()} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Title Bar ──────────────────────────────────────────────────────── */}
      <div className="pa-title-block">
        <div className="pa-title">Purchase Loan Comparison Graphs</div>
        <div className="pa-subtitle">
          Visual loan comparison — total cost, equity built, and payment breakdown over time
        </div>
      </div>

      {/* ── Controls (screen only) ─────────────────────────────────────────── */}
      <div className="g3-controls no-print">
        <div className="g3-control-row">
          <label className="g3-label">Years to compare:</label>
          {yearCols.map((yr, i) => (
            <select key={i} className="g3-select g3-select-sm" value={yr}
              onChange={e => {
                const updated = [...yearCols];
                updated[i] = parseInt(e.target.value, 10);
                setYearCols(updated);
              }}>
              {YEAR_OPTIONS.map(y => (
                <option key={y} value={y}>{y} yr{y !== 1 ? "s" : ""}</option>
              ))}
            </select>
          ))}
        </div>
        <div className="g3-control-row">
          <label className="g3-label">Which graph would you like to see?</label>
          <select className="g3-select" value={graphType}
            onChange={e => setGraphType(e.target.value)}>
            <option value="bar">Color bar graph — total cost comparison</option>
            <option value="donut">Color circle graphs — cost breakdown by loan</option>
            <option value="line">Cost over time — see when loans cross over</option>
            <option value="amort">Payment breakdown — principal vs. interest over loan life</option>
          </select>
          {showStayPicker && (
            <>
              <label className="g3-label">Graph this year:</label>
              <select className="g3-select g3-select-sm" value={safeIdx}
                onChange={e => setGraphYearIdx(parseInt(e.target.value, 10))}>
                {yearCols.map((yr, i) => (
                  <option key={i} value={i}>{yr} year{yr !== 1 ? "s" : ""}</option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      {/* ── Loan Cards Strip ───────────────────────────────────────────────── */}
      {data.length > 0 && (
        <div className="g3-loan-strip">
          {data.map((d, i) => (
            <div key={i} className={`g3-loan-card${i === bestIdx ? " g3-best" : ""}`}
              style={{ borderColor: i === bestIdx ? GREEN : "#dde" }}>
              <div className="g3-card-name"
                style={{ color: i === bestIdx ? GREEN : COLORS[i] || "#444" }}>
                Loan {i + 1}{i === bestIdx ? " ★" : ""}
              </div>
              <div className="g3-card-rate">{d.rate.toFixed(3)}%</div>
              <div className="g3-card-payment">{fmtFull(d.monthlyPI)}/mo</div>
              <div className="g3-card-label">Monthly P&amp;I</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Chart Area ─────────────────────────────────────────────────────── */}
      <div className="g3-chart-area">
        {data.length === 0 ? (
          <div className="g3-empty">No loan data found. Add loans in the Loan Estimate to see graphs.</div>
        ) : (
          <>
            <div className="g3-chart-title">
              {graphType === "bar"   && `Total cost if you sell after ${stayYears} years — P&I payments + closing costs`}
              {graphType === "donut" && `Where your payments go after ${stayYears} years — equity built, interest, and closing costs`}
              {graphType === "line"  && "Total cost over time — lowest line at your selected stay period = best loan"}
              {graphType === "amort" && `How your monthly payment splits — principal (equity) vs. interest — best loan (Loan ${bestIdx + 1})`}
            </div>
            {graphType === "bar"   && <BarChart   data={data} bestIdx={bestIdx} stayYears={stayYears} />}
            {graphType === "donut" && <DonutCharts data={data} bestIdx={bestIdx} />}
            {graphType === "line"  && <LineChart  data={data} bestIdx={bestIdx} stayYears={stayYears} />}
            {graphType === "amort" && <AmortChart  data={data} bestIdx={bestIdx} />}
          </>
        )}
      </div>

      {/* ── Best Loan Callout ──────────────────────────────────────────────── */}
      {data.length > 0 && (
        <div className="g3-best-callout">
          <strong>
            ★ Best loan{showStayPicker ? ` over ${stayYears} years` : ""}:
            &nbsp;Loan {bestIdx + 1}
          </strong>
          &nbsp;at {data[bestIdx].rate.toFixed(3)}%
          &nbsp;|&nbsp;Monthly P&amp;I: {fmtFull(data[bestIdx].monthlyPI)}/mo
          &nbsp;|&nbsp;Closing costs: {fmtFull(data[bestIdx].closing)}
          {showStayPicker && (
            <>&nbsp;|&nbsp;Total {stayYears}-yr cost: {fmtFull(data[bestIdx].totalCost)}</>
          )}
        </div>
      )}

      {/* ── Amortization Explanation ───────────────────────────────────────── */}
      {data.length > 0 && <AmortSection data={data} bestIdx={bestIdx} />}

      {/* ── Disclaimer ─────────────────────────────────────────────────────── */}
      <div className="g3-disclaimer">
        Disclaimer: Totals include P&amp;I payments over the selected time period and include
        closing costs. Equity built is calculated from a standard mortgage amortization
        schedule. This analysis is for informational purposes only and does not constitute
        a loan commitment. &copy; {new Date().getFullYear()} The LANIS Group, LLC.
      </div>

    </div>
  );
}
